/* eslint-disable no-param-reassign */
import { createSelector } from "reselect";
import { DATE_FORMATS, parse } from "utils/Date";
import moment from "moment";
import { initialState } from "./reducer";
import { styling } from "../../styling";
import { getColorDefault } from "../../utils/timeGap";
import { selectPatientDataPageDomain } from "../PatientDataPage/selectors";

/**
 * Direct selector to the app state domain
 */

export const selectAppDomain = state => state.get("app", initialState);

/**
 * Route
 */
const selectRouter = state => state.get("router");

export const makeSelectLocation = () =>
  createSelector(selectRouter, routerState =>
    routerState.get("location").toJS(),
  );

/**
 * Other specific selectors
 */
export const makeSelectSinglePractice = () =>
  createSelector(selectAppDomain, subState => subState.get("singlePractice"));

export const makeSelectFilteredPatients = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("filteredPatients").toJS(),
  );

export const makeSelectActiveStartDate = () =>
  createSelector(selectAppDomain, subState => subState.get("activeStartDate"));

export const makeSelectCurrentMonth = () =>
  createSelector(selectAppDomain, subState => subState.get("currentMonth"));

export const makeSelectFilteredPatientsLoading = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("filteredPatientsLoading"),
  );

// to dentist overview page.
export const makeSelectFilteredDentists = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("filteredDentists").toJS(),
  );

export const makeSelectDentistsLoading = () =>
  createSelector(selectAppDomain, subState => subState.get("dentistsLoading"));

export const makeSelectDentistDeleteFailed = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("dentistFailedMessage"),
  );

export const makeSelectDentistDeleteLoading = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("deleteDentistLoading"),
  );

export const makeSelectFilteredAppointments = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("filteredAppointments").toJS(),
  );

export const makeSelectFilteredAppointmentsLoading = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("filteredAppointmentsLoading"),
  );

export const makeSelectDentistApp = () =>
  createSelector(selectAppDomain, subState => subState.get("dentists").toJS());

export const makeSelectInsurersLoading = () =>
  createSelector(selectPatientDataPageDomain, subState =>
    subState.get("insurersLoading"),
  );

export const makeSelectPracticeProductOrders = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("productOrders").toJS(),
  );

// Messages!
export const makeSelectPracticeMessages = () =>
  createSelector(selectAppDomain, subState => subState.get("messages").toJS());
export const makeSelectMessagesStatusOpenCount = () =>
  createSelector(selectAppDomain, subState => subState.get("statusOpenCount"));

export const makeSelectInsurersApp = () =>
  createSelector(selectAppDomain, subState => subState.get("insurers").toJS());

// Flag treatments for which the next one has 0 minutes
function flagTreatments(treatments) {
  if (treatments.length === 0) {
    return treatments;
  }

  // Loop over every treatment except the last one.
  // Flag the treatments for which the next one has a duration of 0
  for (let i = 0; i < treatments.length - 1; i++) {
    const current = treatments[i];
    const next = treatments[i + 1];

    if (next && next.duration === 0) {
      current.showPopUp = true;
      current.popUpMessage = next.message;
    }
  }

  return treatments;
}

export const makeSelectEndProductTypesConfigurations = () =>
  createSelector(selectAppDomain, subState => {
    const endProducts = subState.get("endProductTypes").toJS();
    return endProducts.endProductTypeConfigurations.map(endProduct => ({
      ...endProduct,
      treatments: flagTreatments(endProduct.treatments),
    }));
  });

export const makeSelectEndProductTypes = () =>
  createSelector(selectAppDomain, subState => {
    const endProducts = subState.get("endProductTypes").toJS();
    return endProducts.endProductTypes.map(endProduct => ({
      ...endProduct,
    }));
  });

export const makeSelectEndProductTypesLoading = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("endProductsLoading"),
  );

export const makeSelectDentistDeleted = () =>
  createSelector(selectAppDomain, subState => subState.get("deleted"));

/**
 * Planned time slots
 */
function sortPlannedTimeSlots(timeSlots) {
  return timeSlots.sort((a, b) => {
    const dateA = parse(a.planned_date_time_from, DATE_FORMATS.BUMBAL);
    const dateB = parse(b.planned_date_time_from, DATE_FORMATS.BUMBAL);

    if (dateA.isBefore(dateB, "day")) {
      return -1;
    }
    if (dateA.isSame(dateB, "day")) {
      return 0;
    }
    return 1;
  });
}

export function filterSortTimeSlots(
  plannedTimeSlots,
  afterToday = false,
  filterDeleted = true,
) {
  // Remove any timeSlots before today if flag is set.
  if (afterToday) {
    const now = moment();
    plannedTimeSlots = plannedTimeSlots.filter(timeSlot => {
      const date = parse(timeSlot.planned_date_time_from, DATE_FORMATS.BUMBAL);

      if (filterDeleted && timeSlot.deleted) {
        return false;
      }

      return date.isAfter(now, "day");
    });
  }

  // Sort the timeSlots
  plannedTimeSlots = sortPlannedTimeSlots(plannedTimeSlots);

  // Calculate occupancy percentage
  plannedTimeSlots = plannedTimeSlots.map(value1 => {
    const percentageValue = (value1.occupiedMin / value1.durationMin) * 100;

    if (Number.isNaN(percentageValue) || !Number.isFinite(percentageValue)) {
      return { ...value1, percent: 0 };
    }

    return { ...value1, percent: percentageValue };
  });

  // Result
  return plannedTimeSlots;
}

export const makeSelectPlannedTimeSlots = (
  afterToday = false,
  filterDeleted = true,
) =>
  createSelector(selectAppDomain, subState => {
    const plannedTimeSlots = subState.get("plannedTimeSlots").toJS();
    return filterSortTimeSlots(plannedTimeSlots, afterToday, filterDeleted);
  });

export const makeSelectPlannedTimeSlotsLoading = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("plannedTimeSlotsLoading"),
  );

/**
 * Get all treatments and parse their from/to date.
 */
export const makeSelectTreatments = () =>
  createSelector(selectAppDomain, subState =>
    subState
      .get("treatments")
      .toJS()
      .map(treatment => ({
        ...treatment,
        planned_date_time_from: parse(
          treatment.planned_date_time_from,
          DATE_FORMATS.BUMBAL,
        ),
        planned_date_time_to: parse(
          treatment.planned_date_time_to,
          DATE_FORMATS.BUMBAL,
        ),
      })),
  );

export const makeSelectTreatmentsLoading = () =>
  createSelector(selectAppDomain, subState =>
    subState.get("treatmentsLoading"),
  );

export const makeSelectPlannedDates = () =>
  createSelector(selectAppDomain, subState => {
    const {
      treatments = [],
      timeSlots = [],
      productOrders = [],
    } = subState.toJS();
    const dates = [];

    // Loop timeSlots to find dates.
    timeSlots.forEach(timeSlot => {
      const date = parse(timeSlot.planned_date_time_from, DATE_FORMATS.BUMBAL);
      if (!dates.find(d => d.date.isSame(date, "days"))) {
        dates.push({
          date,
          color: getColorDefault(
            timeSlot.durationMin,
            timeSlot.occupiedMin,
            timeSlot.maxGapMin,
          ),
        });
      }
    });

    // Loop treatments to find dates.
    treatments.forEach(treatment => {
      const date = parse(treatment.planned_date_time_from, DATE_FORMATS.BUMBAL);
      if (!dates.find(d => d.date.isSame(date, "days"))) {
        dates.push({ date, color: styling.blue });
      }
    });

    productOrders.forEach(po => {
      const date = moment.unix(po.deliveryDate);
      if (!dates.find(d => d.date.isSame(date, "days"))) {
        dates.push({ date, color: styling.black });
      }
    });

    dates.sort((a, b) => a.date.unix() - b.date.unix());
    return dates;
  });

export function groupPlannedTimeSlots(timeSlots) {
  const timeSlotObj = {};

  timeSlots.forEach(timeSlot => {
    const date = timeSlot.planned_date_time_from.split(" ")[0];

    if (!timeSlotObj[date]) {
      timeSlotObj[date] = [];
    }
    timeSlotObj[date].push(timeSlot);
  });

  return timeSlotObj;
}

export const makeSelectPlannedTimeSlotsGrouped = () =>
  createSelector(selectAppDomain, subState => {
    const timeSlots = subState.get("plannedTimeSlots").toJS();
    return groupPlannedTimeSlots(timeSlots);
  });

/**
 * Default selector used by App
 */
const makeSelectApp = () =>
  createSelector(selectAppDomain, subState => subState.toJS());
export default makeSelectApp;
